import {
  EnergyPulse,
  RADIUS,
  SHADOW,
  Turnstile,
  WithHalfdot,
  Wordmark,
} from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useLocale } from "~/i18n";
import { requestOtp } from "~/lib/api";
import { KEYBOARD_INSET_VAR } from "~/lib/keyboard-inset";
import { useAuthStore } from "~/store/auth-store";

import {
  AUTH_CODE_PATH,
  type CodeStepState,
  translateAuthError,
} from "./auth-flow";

/**
 * Step one of passwordless admin sign-in: the approved e-mail address the code will
 * go to. The code itself lives on `VerifyScreen`, pushed onto browser history so both
 * its back control and the browser's back button return here.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The lower sheet keeps enough height on a phone to dock the primary action near the
 * bottom edge. The keyboard inset gives that floor back when the keys need the room.
 */
const SHEET_HEIGHT = "54dvh";

export function LoginScreen() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const config = useAuthStore((state) => state.config);

  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  // Bumping this remounts the widget, which is the only way to get a new token —
  // Turnstile tokens are single-use and the previous one is spent by the request.
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const captchaRequired = config?.captcha_required ?? false;

  const handleCaptcha = useCallback(
    (token: string | null) => setCaptchaToken(token),
    [],
  );

  const submitEmail = async (event: FormEvent) => {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) {
      setErrorKey("auth.errors.invalidEmail");
      return;
    }

    setBusy(true);
    setErrorKey(null);
    try {
      const response = await requestOtp(email.trim(), {
        captchaToken: captchaToken ?? undefined,
        locale,
      });
      const state: CodeStepState = {
        email: email.trim(),
        expiresIn: response.expires_in,
        debugCode: response.debug_code,
      };
      navigate(AUTH_CODE_PATH, { state });
    } catch (error) {
      setErrorKey(translateAuthError(error));
    } finally {
      setBusy(false);
      // The token was consumed by the request above, whether or not it succeeded.
      if (captchaRequired) {
        setCaptchaToken(null);
        setCaptchaNonce((nonce) => nonce + 1);
      }
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100dvh",
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        // Everything in flow is laid out inside what the keyboard leaves. The pulse
        // is positioned independently, so it stays put as the form moves.
        paddingBottom: `var(${KEYBOARD_INSET_VAR}, 0px)`,
      }}
    >
      {/* The admin keeps its ambient Energy Pulse instead of the member video. */}
      <EnergyPulse
        size={420}
        opacity={0.28}
        blur={12}
        sx={{ position: "absolute", top: -160, right: -140, zIndex: 0 }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          paddingInline: 3,
          paddingTop: "calc(28px + env(safe-area-inset-top))",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Wordmark size={26} />
        <Typography variant="OV1" sx={{ color: "text.muted" }}>
          {t("auth.eyebrow")}
        </Typography>
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          marginTop: "auto",
          marginBottom: { md: "auto" },
          marginInline: { md: "auto" },
          maxWidth: { md: 460 },
          minHeight: {
            xs: `max(0px, calc(${SHEET_HEIGHT} - var(${KEYBOARD_INSET_VAR}, 0px)))`,
            md: 0,
          },
          backgroundColor: "background.paper",
          borderRadius: {
            xs: `${RADIUS.xl}px ${RADIUS.xl}px 0 0`,
            md: `${RADIUS.xl}px`,
          },
          boxShadow: SHADOW.raised,
          display: "flex",
          flexDirection: "column",
          padding: 3,
          paddingTop: 4,
          paddingBottom: {
            xs: "calc(24px + env(safe-area-inset-bottom))",
            md: 3,
          },
        }}
      >
        <Box
          component="form"
          onSubmit={submitEmail}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            flex: 1,
          }}
        >
          <Box>
            <Typography variant="H1" sx={{ color: "text.primary" }}>
              <WithHalfdot size={12}>{t("auth.title")}</WithHalfdot>
            </Typography>
            <Typography
              variant="T2"
              sx={{ color: "text.secondary", marginTop: 1.5 }}
            >
              {t("auth.subtitle")}
            </Typography>
          </Box>

          <TextField
            label={t("auth.emailLabel")}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            autoFocus
            required
            fullWidth
            slotProps={{
              // Still required — the asterisk is just noise on a lone field.
              inputLabel: { required: false },
              htmlInput: { "data-testid": "auth.email", inputMode: "email" },
            }}
          />

          {captchaRequired ? (
            <Turnstile key={captchaNonce} onToken={handleCaptcha} />
          ) : null}

          {errorKey ? <Alert severity="error">{t(errorKey)}</Alert> : null}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy || (captchaRequired && !captchaToken)}
            data-testid="auth.send-code"
            sx={{ marginTop: "auto" }}
          >
            {t("common.continue")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
